import React, { useState } from 'react';
import { Settings as SettingsIcon, User, Bell, Shield, Palette, Globe } from 'lucide-react';
import { usePageHeader } from '../contexts/PageHeaderContext';
import PageHeader from '../components/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle, { ThemeSwitch } from '../components/ThemeToggle';
import { showToast } from '../utils/toast';

// Page Header configuration
const PAGE_HEADER_CONFIG = {
  breadcrumbs: [{ label: 'Dashboard', path: '/' }, { label: 'Settings' }],
  title: 'Settings',
  icon: '⚙️',
  description: 'Manage your account settings and preferences.',
};

const Settings = () => {
  const { theme, setTheme, availableThemes } = useTheme();
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: false,
  });
  
  usePageHeader(PAGE_HEADER_CONFIG);

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    showToast(`${key} notifications ${notifications[key] ? 'disabled' : 'enabled'}`, 'success');
  };

  const handleThemeChange = (themeName) => {
    setTheme(themeName);
    showToast(`Theme changed to ${themeName}`, 'success');
  };

  return (
    <div className="space-y-6">
      <PageHeader {...PAGE_HEADER_CONFIG} />
      {/* Profile Settings */}
      <div 
        className="rounded-xl p-6 shadow-sm transition-colors duration-300"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center mb-6">
          <div 
            className="p-3 rounded-lg mr-4"
            style={{ backgroundColor: 'var(--color-primary-light)' }}
          >
            <User className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
          </div>
          <div>
            <h3 
              className="text-lg font-semibold"
              style={{ color: 'var(--color-text)' }}
            >
              Profile Settings
            </h3>
            <p 
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Manage your personal information
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--color-text)' }}
            >
              Full Name
            </label>
            <input
              type="text"
              defaultValue="John Doe"
              className="w-full px-4 py-2 rounded-lg border transition-colors"
              style={{
                backgroundColor: 'var(--color-surface)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text)',
              }}
            />
          </div>
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--color-text)' }}
            >
              Email
            </label>
            <input
              type="email"
              defaultValue="john.doe@example.com"
              className="w-full px-4 py-2 rounded-lg border transition-colors"
              style={{
                backgroundColor: 'var(--color-surface)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text)',
              }}
            />
          </div>
          <button
            onClick={() => showToast('Profile updated successfully!', 'success')}
            className="px-6 py-2 rounded-lg text-white font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Theme Settings */}
      <div 
        className="rounded-xl p-6 shadow-sm transition-colors duration-300"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center mb-6">
          <div 
            className="p-3 rounded-lg mr-4"
            style={{ backgroundColor: 'var(--color-primary-light)' }}
          >
            <Palette className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
          </div>
          <div>
            <h3 
              className="text-lg font-semibold"
              style={{ color: 'var(--color-text)' }}
            >
              Theme Settings
            </h3>
            <p 
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Customize your appearance
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label 
              className="block text-sm font-medium mb-3"
              style={{ color: 'var(--color-text)' }}
            >
              Select Theme
            </label>
            <div className="grid grid-cols-3 gap-4">
              {availableThemes.map((themeName) => {
                const themeData = availableThemes.find(t => t === themeName);
                const isActive = theme.name === themeName;
                
                return (
                  <button
                    key={themeName}
                    onClick={() => handleThemeChange(themeName)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isActive ? 'ring-2' : ''
                    }`}
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      borderColor: isActive ? 'var(--color-primary)' : 'var(--color-border)',
                      ringColor: 'var(--color-primary)',
                    }}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">
                        {themeName === 'light' ? '☀️' : themeName === 'dark' ? '🌙' : '🎨'}
                      </div>
                      <div 
                        className="text-sm font-medium capitalize"
                        style={{ color: 'var(--color-text)' }}
                      >
                        {themeName}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div>
              <p 
                className="text-sm font-medium mb-1"
                style={{ color: 'var(--color-text)' }}
              >
                Quick Toggle
              </p>
              <p 
                className="text-xs"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Toggle between light and dark mode
              </p>
            </div>
            <ThemeSwitch />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div 
        className="rounded-xl p-6 shadow-sm transition-colors duration-300"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center mb-6">
          <div 
            className="p-3 rounded-lg mr-4"
            style={{ backgroundColor: 'var(--color-primary-light)' }}
          >
            <Bell className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
          </div>
          <div>
            <h3 
              className="text-lg font-semibold"
              style={{ color: 'var(--color-text)' }}
            >
              Notification Settings
            </h3>
            <p 
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Manage how you receive notifications
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { key: 'email', label: 'Email Notifications', description: 'Receive notifications via email' },
            { key: 'push', label: 'Push Notifications', description: 'Receive browser push notifications' },
            { key: 'sms', label: 'SMS Notifications', description: 'Receive notifications via SMS' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface-hover)' }}>
              <div className="flex-1">
                <p 
                  className="text-sm font-medium mb-1"
                  style={{ color: 'var(--color-text)' }}
                >
                  {item.label}
                </p>
                <p 
                  className="text-xs"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {item.description}
                </p>
              </div>
              <button
                onClick={() => handleNotificationChange(item.key)}
                className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                  notifications[item.key] ? '' : 'opacity-50'
                }`}
                style={{
                  backgroundColor: notifications[item.key] ? 'var(--color-primary)' : 'var(--color-border)',
                }}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
                    notifications[item.key] ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Security Settings */}
      <div 
        className="rounded-xl p-6 shadow-sm transition-colors duration-300"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center mb-6">
          <div 
            className="p-3 rounded-lg mr-4"
            style={{ backgroundColor: 'var(--color-primary-light)' }}
          >
            <Shield className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
          </div>
          <div>
            <h3 
              className="text-lg font-semibold"
              style={{ color: 'var(--color-text)' }}
            >
              Security Settings
            </h3>
            <p 
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Manage your account security
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => showToast('Password reset email sent!', 'info')}
            className="w-full px-4 py-3 rounded-lg border transition-colors text-left hover:bg-opacity-50"
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface)';
            }}
          >
            <p className="font-medium">Change Password</p>
            <p 
              className="text-sm mt-1"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Update your account password
            </p>
          </button>
          <button
            onClick={() => showToast('Two-factor authentication enabled!', 'success')}
            className="w-full px-4 py-3 rounded-lg border transition-colors text-left hover:bg-opacity-50"
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface)';
            }}
          >
            <p className="font-medium">Two-Factor Authentication</p>
            <p 
              className="text-sm mt-1"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Add an extra layer of security
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
